// =====================================================================
//  Conversions paquet <-> modèles du back-office
//  ---------------------------------------------------------------------
//  Seul endroit qui connaît la correspondance entre le format d'échange
//  (camelCase, enums en chaînes) et les modèles BO (PascalCase, enums en
//  entiers alignés sur le C#).
// =====================================================================

import {
    IEasymailPackage,
    IPackageOtpStrategy,
    IPackageTemplate,
    IPackageTranslation,
    PACKAGE_FORMAT,
    PACKAGE_FILE_EXTENSION,
    PackageOtpAlphabet,
    PackageOtpCase,
    PackageOtpChannel,
    PackageTypeContent,
    PackageTypeProcess,
} from "../../Model/Package/IEasymailPackage.js";
import {ITemplateModel} from "../../Model/Template/ITemplateModel.js";
import {IOtpStrategyModel, OtpAlphabet, OtpCase, OtpChannel, OtpChannelMask} from "../../Model/Otp/OtpStrategyModel.js";
import {TypeContentEnum} from "../../Variables/TypeContentEnum.js";
import {TypeProcessEnum} from "../../Variables/TypeProcessEnum.js";
import {String} from "../Misc.js";

const MODEL_TEST_NOM = "Modèle de test";

export class PackageMapper {

    // --- Enums ---------------------------------------------------------------

    static TypeContentToInt = (v: PackageTypeContent): number =>
        v === "text" ? TypeContentEnum.Instance.Text : TypeContentEnum.Instance.Html;

    static TypeContentFromInt = (v: number): PackageTypeContent =>
        v === TypeContentEnum.Instance.Text ? "text" : "html";

    static TypeProcessToInt = (v: PackageTypeProcess | undefined): number => {
        switch (v) {
            case "html":
                return TypeProcessEnum.Instance.Html;
            case "pdf":
                return TypeProcessEnum.Instance.Pdf;
            default:
                return TypeProcessEnum.Instance.Mail;
        }
    };

    /** PdfAndMail n'est pas représentable (non implémenté côté API) : renvoie undefined. */
    static TypeProcessFromInt = (v: number): PackageTypeProcess | undefined => {
        switch (v) {
            case TypeProcessEnum.Instance.Mail:
                return "mail";
            case TypeProcessEnum.Instance.Html:
                return "html";
            case TypeProcessEnum.Instance.Pdf:
                return "pdf";
            default:
                return undefined;
        }
    };

    static readonly #alphabets: Array<[PackageOtpAlphabet, OtpAlphabet]> = [
        ["numeric", OtpAlphabet.Numeric],
        ["alphanumeric", OtpAlphabet.Alphanumeric],
        ["alphanumericNoAmbiguous", OtpAlphabet.AlphanumericNoAmbiguous],
    ];

    static readonly #cases: Array<[PackageOtpCase, OtpCase]> = [
        ["upper", OtpCase.Upper],
        ["lower", OtpCase.Lower],
        ["mixed", OtpCase.Mixed],
    ];

    static AlphabetToInt = (v: PackageOtpAlphabet): OtpAlphabet =>
        PackageMapper.#alphabets.find(x => x[0] === v)?.[1] ?? OtpAlphabet.Numeric;

    static AlphabetFromInt = (v: OtpAlphabet | undefined): PackageOtpAlphabet =>
        PackageMapper.#alphabets.find(x => x[1] === v)?.[0] ?? "numeric";

    static CaseToInt = (v: PackageOtpCase | undefined): OtpCase =>
        PackageMapper.#cases.find(x => x[0] === v)?.[1] ?? OtpCase.Upper;

    static CaseFromInt = (v: OtpCase | undefined): PackageOtpCase =>
        PackageMapper.#cases.find(x => x[1] === v)?.[0] ?? "upper";

    static ChannelsToMask = (channels: Array<PackageOtpChannel>): number =>
        channels.reduce((mask, c) => OtpChannelMask.Set(mask, c === "sms" ? OtpChannel.Sms : OtpChannel.Mail, true), 0);

    static ChannelsFromMask = (mask: number | undefined): Array<PackageOtpChannel> => {
        const out: Array<PackageOtpChannel> = [];
        if (OtpChannelMask.Has(mask, OtpChannel.Mail)) out.push("mail");
        if (OtpChannelMask.Has(mask, OtpChannel.Sms)) out.push("sms");
        return out;
    };

    // --- Paquet -> modèles ---------------------------------------------------

    /** Sérialise le modelTest tel qu'il est stocké dans MODEL_TEST.CODE. */
    static ModelTestToCode = (pkg: IEasymailPackage): string =>
        JSON.stringify(pkg.modelTest ?? {}, null, 2);

    /** Pivot (fr) prêt à être créé dans le projet `fkProjet`. */
    static ToNewPivot = (pkg: IEasymailPackage, fkProjet: string): ITemplateModel => {
        const t = pkg.template;
        return {
            Primarykey: String.GetEmptyGuid,
            FkProjet: fkProjet,
            Nom: t.name,
            Libelle: t.label ?? "",
            TypeContent: PackageMapper.TypeContentToInt(t.typeContent),
            TypeProcess: PackageMapper.TypeProcessToInt(t.typeProcess),
            SubjectTemplate: t.subject,
            BodyTemplate: t.body,
            ExpediteurEmail: t.sender?.email,
            ExpediteurNom: t.sender?.name,
            Langage: "fr",
            Datas: [{Nom: MODEL_TEST_NOM, Code: PackageMapper.ModelTestToCode(pkg)}],
        };
    };

    /** Traduction prête à être créée sous le pivot `pivot`. */
    static ToNewTranslation = (tr: IPackageTranslation, pivot: ITemplateModel): ITemplateModel => ({
        ...pivot,
        Primarykey: String.GetEmptyGuid,
        FkParent: pivot.Primarykey,
        Langage: tr.language,
        SubjectTemplate: tr.subject,
        BodyTemplate: tr.body,
        Datas: undefined,
        Translations: undefined,
    });

    /** Stratégie OTP prête à être créée, rattachée au template mail `fkMailTemplate`. */
    static ToNewOtpStrategy = (s: IPackageOtpStrategy, fkProjet: string, fkMailTemplate: string): IOtpStrategyModel => {
        const m: IOtpStrategyModel = {FkProjet: fkProjet, FkMailTemplate: fkMailTemplate};
        PackageMapper.ApplyOtpStrategy(s, m);
        return m;
    };

    /** Recopie la configuration du paquet dans un modèle de stratégie existant (en place). */
    static ApplyOtpStrategy = (s: IPackageOtpStrategy, m: IOtpStrategyModel): void => {
        m.Nom = s.name;
        m.Libelle = s.label ?? "";
        m.PatternLength = s.pattern.length;
        m.PatternAlphabet = PackageMapper.AlphabetToInt(s.pattern.alphabet);
        m.PatternCase = PackageMapper.CaseToInt(s.pattern.case);
        m.PatternGroupSize = s.pattern.groupSize ?? 0;
        m.PatternGroupSeparator = s.pattern.groupSeparator ?? (m.PatternGroupSize > 0 ? "-" : "");
        m.ExpirationMinutes = s.expirationMinutes;
        m.MaxAttempts = s.maxAttempts;
        m.Channels = PackageMapper.ChannelsToMask(s.channels);
    };

    // --- Modèles -> paquet ---------------------------------------------------

    /**
     * Construit un paquet à partir d'un pivot (avec Datas) et, en option,
     * de ses traductions et de la stratégie OTP qui l'utilise.
     */
    static FromModels = (options: IPackageExportOptions): IEasymailPackage => {
        const p = options.pivot;
        const template: IPackageTemplate = {
            name: p.Nom ?? "",
            ...(String.IsNullOrEmpty(p.Libelle) ? {} : {label: p.Libelle}),
            typeContent: PackageMapper.TypeContentFromInt(p.TypeContent),
            typeProcess: PackageMapper.TypeProcessFromInt(p.TypeProcess) ?? "mail",
            subject: p.SubjectTemplate ?? "",
            body: p.BodyTemplate ?? "",
        };
        if (!String.IsNullOrEmpty(p.ExpediteurEmail))
            template.sender = {
                email: p.ExpediteurEmail,
                ...(String.IsNullOrEmpty(p.ExpediteurNom) ? {} : {name: p.ExpediteurNom}),
            };

        const pkg: IEasymailPackage = {
            format: PACKAGE_FORMAT,
            meta: {
                generator: "easymail-backoffice",
                createdAt: new Date().toISOString(),
                source: {
                    templateId: p.Primarykey,
                    ...(options.otpStrategy?.Primarykey ? {otpStrategyId: options.otpStrategy.Primarykey} : {}),
                    ...(options.environment ? {environment: options.environment} : {}),
                    exportedAt: new Date().toISOString(),
                },
            },
            template,
            modelTest: PackageMapper.#parseModelTest(p),
        };

        const translations = (options.translations ?? [])
            .filter(t => t.Langage && t.Langage !== "fr")
            .map(t => ({language: t.Langage, subject: t.SubjectTemplate ?? "", body: t.BodyTemplate ?? ""}));
        if (translations.length)
            pkg.translations = translations;

        const s = options.otpStrategy;
        if (s)
            pkg.otpStrategy = {
                name: s.Nom ?? "",
                ...(String.IsNullOrEmpty(s.Libelle) ? {} : {label: s.Libelle}),
                pattern: {
                    length: s.PatternLength ?? 6,
                    alphabet: PackageMapper.AlphabetFromInt(s.PatternAlphabet),
                    case: PackageMapper.CaseFromInt(s.PatternCase),
                    groupSize: s.PatternGroupSize ?? 0,
                    ...(String.IsNullOrEmpty(s.PatternGroupSeparator) ? {} : {groupSeparator: s.PatternGroupSeparator}),
                },
                expirationMinutes: s.ExpirationMinutes ?? 10,
                maxAttempts: s.MaxAttempts ?? 5,
                channels: PackageMapper.ChannelsFromMask(s.Channels),
            };

        return pkg;
    };

    /** Le modèle de test stocké est une chaîne JSON : l'export échoue explicitement s'il est invalide. */
    static #parseModelTest = (p: ITemplateModel): Record<string, unknown> => {
        const code = p.Datas?.[0]?.Code;
        if (String.IsNullOrEmpty(code))
            return {};
        try {
            const parsed = JSON.parse(code);
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed))
                return parsed;
        } catch {
            // traité ci-dessous
        }
        throw new Error("Le modèle de données (onglet « Modèle ») n'est pas un objet JSON valide : corrigez-le avant d'exporter.");
    };

    /** Nom de fichier : nom du template en kebab-case + extension du format. */
    static FileName = (pkg: IEasymailPackage): string => {
        const base = (pkg.otpStrategy?.name || pkg.template.name || "template")
            .normalize("NFD").replace(/[̀-ͯ]/g, "")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "") || "template";
        return base + PACKAGE_FILE_EXTENSION;
    };

    static Stringify = (pkg: IEasymailPackage): string => JSON.stringify(pkg, null, 2) + "\n";
}

export interface IPackageExportOptions {
    pivot: ITemplateModel;
    translations?: Array<ITemplateModel>;
    otpStrategy?: IOtpStrategyModel;
    environment?: string;
}
